import React, { useEffect, useMemo } from "react";
import "./AntivirusRage.css";
import {
  FaShieldAlt,
  FaBug,
  FaLock,
  FaBolt,
  FaSyncAlt,
  FaWindows,
  FaApple,
  FaAndroid,
  FaCheckCircle,
  FaGlobe,
} from "react-icons/fa";

import { FaTriangleExclamation } from "react-icons/fa6";

import { useLocalContext } from "../../context/LocalContext";

const AntivirusRage = () => {
  const { webinfo = {} } = useLocalContext();
  const brandName = webinfo.name || "AntivirusRage";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const howItWorks = useMemo(
    () => [
      {
        icon: <FaBolt />,
        title: "Real-time monitoring",
        desc: "Watches files and apps as they run, helping detect threats before they can do damage.",
      },
      {
        icon: <FaBug />,
        title: "Threat detection (signatures + behavior)",
        desc: "Uses known threat patterns plus suspicious behavior detection to catch new or modified malware.",
      },
      {
        icon: <FaSyncAlt />,
        title: "Frequent updates",
        desc: "Receives updates for new malware definitions and security improvements.",
      },
      {
        icon: <FaLock />,
        title: "Web / phishing protection",
        desc: "Can warn about suspicious websites, unsafe downloads, and phishing pages designed to steal data.",
      },
    ],
    []
  );

  const protectsAgainst = useMemo(
    () => [
      { icon: <FaCheckCircle />, title: "Viruses & worms", desc: "Self-replicating malicious programs." },
      { icon: <FaCheckCircle />, title: "Trojans", desc: "Malware disguised as legitimate files/apps." },
      { icon: <FaCheckCircle />, title: "Spyware", desc: "Tracks activity or steals information silently." },
      { icon: <FaCheckCircle />, title: "Ransomware", desc: "Locks/encrypts data and demands payment." },
      { icon: <FaCheckCircle />, title: "Adware & PUPs", desc: "Unwanted programs that change settings or show ads." },
      { icon: <FaCheckCircle />, title: "Phishing attempts", desc: "Fake pages/emails to steal passwords or OTPs." },
    ],
    []
  );

  const safeHabits = useMemo(
    () => [
      "Keep your operating system and browser updated.",
      "Install apps only from trusted sources (official stores/sites).",
      "Avoid clicking unknown links in SMS/WhatsApp/email.",
      "Use strong, unique passwords + a password manager if possible.",
      "Enable 2FA (two-factor authentication) on important accounts.",
      "Back up important files (cloud or external drive) regularly.",
    ],
    []
  );

  const warningSigns = useMemo(
    () => [
      "Browser redirects or new toolbars/extensions you didn’t install",
      "Frequent pop-ups or “security alerts” that look suspicious",
      "Device suddenly slower, overheating, or high CPU usage",
      "Unknown apps installed automatically",
      "Accounts getting logged out or unusual login alerts",
    ],
    []
  );

  const myths = useMemo(
    () => [
      {
        q: "“I use an iPhone/Mac, so I don’t need security.”",
        a: "These platforms are generally safer, but phishing, unsafe links, and malicious profiles/extensions can still cause issues. Safe browsing habits matter everywhere.",
      },
      {
        q: "“Antivirus guarantees 100% protection.”",
        a: "No tool can guarantee 100%. Antivirus reduces risk, but updates + safe habits + backups are equally important.",
      },
      {
        q: "“If I don’t download files, I’m safe.”",
        a: "Many threats spread through phishing links, malicious ads, fake login pages, and compromised websites—downloads aren’t the only route.",
      },
    ],
    []
  );

  const faqs = useMemo(
    () => [
      {
        q: "What is antivirus software?",
        a: "Antivirus is a security tool that helps detect, block, and remove malicious software (malware) from your device.",
      },
      {
        q: "What’s the difference between antivirus and anti-malware?",
        a: "In practice today, most “antivirus” tools include broader anti-malware protection (viruses, trojans, ransomware, spyware, etc.).",
      },
      {
        q: "Do built-in protections (like Windows Security) help?",
        a: "Yes—built-in protections have improved a lot. Whether you need extra tools depends on your browsing habits, device usage, and risk level.",
      },
      {
        q: "Why do some antivirus tools slow down devices?",
        a: "Heavy background scans, outdated machines, multiple security tools running together, or aggressive settings can reduce performance.",
      },
      {
        q: "What should I do if I think I’m infected?",
        a: "Disconnect from the internet if needed, run a full scan, remove suspicious apps/extensions, update passwords, and restore from backup if the damage is severe.",
      },
    ],
    []
  );

  return (
    <main className="avr" aria-label="Antivirus information page">
      {/* HERO */}
      <section className="avr-hero" aria-label="Antivirus basics">
        <div className="avr-heroBg" aria-hidden="true" />
        <div className="avr-wrap">
          <div className="avr-heroGrid">
            <div className="avr-heroText">
              <p className="avr-kicker">
                <span className="avr-dot" aria-hidden="true" />
                Antivirus Basics
              </p>

              <h1 className="avr-title">
                A simple guide to antivirus: what it is, what it does, and how to stay safe online.
              </h1>

              <p className="avr-sub">
                This page is informational. It explains common antivirus concepts, what threats look like,
                and practical safety habits for everyday users.
              </p>

              <div className="avr-badges" aria-label="Supported platforms">
                <span className="avr-badge">
                  <FaWindows /> Windows
                </span>
                <span className="avr-badge">
                  <FaApple /> macOS
                </span>
                <span className="avr-badge">
                  <FaAndroid /> Android
                </span>
                <span className="avr-badge">
                  <FaGlobe /> Web safety
                </span>
              </div>

              <p className="avr-mini">
                Prepared for <strong>{brandName}</strong> visitors — updated guidance + safe basics.
              </p>
            </div>

            <aside className="avr-card" aria-label="Quick overview">
              <div className="avr-cardTop">
                <div className="avr-cardIcon" aria-hidden="true">
                  <FaShieldAlt />
                </div>
                <div>
                  <div className="avr-cardTitle">Quick overview</div>
                  <div className="avr-cardSub">What antivirus typically covers</div>
                </div>
              </div>

              <div className="avr-list">
                <div className="avr-li">
                  <FaCheckCircle className="ok" />
                  <span>Detects & blocks common malware</span>
                </div>
                <div className="avr-li">
                  <FaCheckCircle className="ok" />
                  <span>Warns about unsafe sites & downloads</span>
                </div>
                <div className="avr-li">
                  <FaCheckCircle className="ok" />
                  <span>Helps remove infections via scans</span>
                </div>
                <div className="avr-li">
                  <FaCheckCircle className="ok" />
                  <span>Improves safety with updates</span>
                </div>
              </div>

              <p className="avr-mini">
                Antivirus is strongest when combined with updates, cautious clicking, and backups.
              </p>
            </aside>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="avr-sec" aria-label="How antivirus works">
        <div className="avr-wrap">
          <header className="avr-head">
            <h2>How antivirus typically works</h2>
            <p>
              Different tools vary, but most modern antivirus products use a combination of scanning,
              real-time monitoring, and web protection.
            </p>
          </header>

          <div className="avr-featGrid" role="list">
            {howItWorks.map((f) => (
              <article key={f.title} className="avr-feat" role="listitem">
                <div className="avr-featIcon" aria-hidden="true">
                  {f.icon}
                </div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT IT PROTECTS */}
      <section className="avr-sec avr-secAlt" aria-label="What antivirus protects against">
        <div className="avr-wrap">
          <header className="avr-head">
            <h2>What antivirus helps protect against</h2>
            <p>
              These are common categories. Real-world threats often overlap (for example, a trojan that
              installs spyware).
            </p>
          </header>

          <div className="avr-protectGrid" role="list">
            {protectsAgainst.map((x) => (
              <div key={x.title} className="avr-protect" role="listitem">
                <div className="avr-protectIcon" aria-hidden="true">
                  {x.icon}
                </div>
                <div>
                  <h3>{x.title}</h3>
                  <p>{x.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SAFE HABITS */}
      <section className="avr-sec" aria-label="Safety habits">
        <div className="avr-wrap">
          <header className="avr-head">
            <h2>Simple safety habits that reduce risk</h2>
            <p>These are small steps that protect you even if antivirus misses something.</p>
          </header>

          <ul className="avr-bullets">
            {safeHabits.map((t) => (
              <li key={t}>
                <FaCheckCircle className="ok" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* WARNING SIGNS */}
      <section className="avr-sec avr-secAlt" aria-label="Warning signs of infection">
        <div className="avr-wrap">
          <header className="avr-head">
            <h2>Common warning signs</h2>
            <p>Not every slow device is infected—but these are signals worth checking.</p>
          </header>

          <ul className="avr-bullets avr-bulletsWarn">
            {warningSigns.map((t) => (
              <li key={t}>
                <FaTriangleExclamation className="warn" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* MYTHS */}
      <section className="avr-sec" aria-label="Myths and facts">
        <div className="avr-wrap">
          <header className="avr-head">
            <h2>Myths & facts</h2>
            <p>Quick clarifications to avoid common mistakes.</p>
          </header>

          <div className="avr-faq">
            {myths.map((x) => (
              <details key={x.q} className="avr-faqItem">
                <summary>{x.q}</summary>
                <p>{x.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="avr-sec avr-secAlt" aria-label="FAQ">
        <div className="avr-wrap">
          <header className="avr-head">
            <h2>FAQ</h2>
            <p>Basic questions about antivirus and everyday security.</p>
          </header>

          <div className="avr-faq">
            {faqs.map((x) => (
              <details key={x.q} className="avr-faqItem">
                <summary>{x.q}</summary>
                <p>{x.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default AntivirusRage;
